import {
    GetObjectCommand,
    GetObjectCommandInput,
    PutObjectCommand,
    S3Client,
} from "@aws-sdk/client-s3"
import { getSignedUrl } from "@aws-sdk/s3-request-presigner"
import { config } from "@nova/configuration/node"
import { SupportedRegions } from "@nova/constants"
import { getDataBucket, getSampleDataBucket } from "./buckets"
import { omitUndefined } from "nodash"

export enum ExpireTypes {
    video = "video",
    pdf = "pdf",
    pano = "pano",
    pdfRaster = "pdfRaster",
    projectThumbnail = "projectThumbnail",
}

const expireMap: Record<ExpireTypes, number> = {
    video: 86400,
    pdf: 129600, // 36 hours
    pano: 86400,
    pdfRaster: 86400,
    projectThumbnail: 86400,
}
const defaultSignedUrlExpiresInSeconds = 600
const defaultRegion = config.get("awsDefaultRegion") as SupportedRegions

export async function getSignedDownloadUrl(
    key: string,
    {
        expireKey,
        region = defaultRegion,
        attachment = "",
        isDemo = false,
        bucket = getDataBucket(region),
    }: {
        expireKey?: ExpireTypes
        region?: SupportedRegions
        attachment?: string
        isDemo?: boolean
        bucket?: string
    } = {}
) {
    if (isDemo) {
        const bucketName = getSampleDataBucket(region)
        return { url: `https://${bucketName}.s3.${region}.amazonaws.com/${key}`, expires: -1 }
    }

    const keyParts = key.split("/")
    if (keyParts.length >= 4 && keyParts[1] === "user") {
        // user data is only stored in ca-central-1
        region = SupportedRegions.Canada
    }

    const input: GetObjectCommandInput = {
        Bucket: bucket,
        Key: key,
    }
    if (attachment) {
        // browser will try to open file by default
        input.ResponseContentDisposition = `inline; filename="${encodeURIComponent(attachment)}"`
    }

    const isValidRegion = Object.values(SupportedRegions).includes(region)
    if (!isValidRegion) {
        throw "Invalid region set"
    }

    const client = new S3Client({ region })
    const command = new GetObjectCommand(input)
    const expiresIn = expireKey ? expireMap[expireKey] : defaultSignedUrlExpiresInSeconds
    const expires = Math.round(Date.now() / 1000) + expiresIn

    const url = await getSignedUrl(client, command, {
        expiresIn,
    })

    return {
        url,
        expires,
    }
}

/**
 * sets up a presigned url that one can use to upload a file
 *
 * @param key - string
 * @param region - where the upload should be stored
 * @param metadata - optional key-value pairs to attach to the S3 object
 * @returns { url, expires, region }
 */
export async function getSignedUploadUrl(
    key: string,
    region: SupportedRegions,
    bucket: string,
    tags?: Record<string, string | undefined>,
    ttlSeconds?: number
): Promise<{ url: string; expires: number; tagging?: string }> {
    // Intentionally not using URLSearchParams as it will encode "=" chars and then AWS encodes "%3D" again, stripping "=" chars.
    const tagging = tags
        ? Object.entries(omitUndefined(tags))
              .map(([k, v]) => `${k}=${v}`)
              .join("&")
        : undefined

    const input = {
        Bucket: bucket,
        Key: key,
        ...(tagging && { Tagging: tagging }),
    }

    const isValidRegion = Object.values(SupportedRegions).includes(region)
    if (!isValidRegion) {
        throw "Invalid region set"
    }

    // we can't change the s3 region on the fly, so need to define the client on each request - is there a better way to do it?
    const client = new S3Client({ region, requestChecksumCalculation: "WHEN_REQUIRED" })
    const command = new PutObjectCommand(input)
    const expiresIn = key.toLowerCase().endsWith(".mp4")
        ? expireMap["video"]
        : (ttlSeconds ?? defaultSignedUrlExpiresInSeconds)
    const expires = Math.round(Date.now() / 1000) + expiresIn

    const url = await getSignedUrl(client, command, {
        expiresIn,
    })

    return {
        url,
        expires,
        tagging,
    }
}
